import compact from 'lodash/compact';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import {
  CalendarDate,
  calendarDateFromTimestamp,
  compareCalendarDates,
  isAfterOrEqual,
  isBeforeOrEqual,
  toCalendarDate,
  today,
} from '../dateutils';
import {
  getLeaseFixedReferences,
  isInvoiceIssuedWithItsOwnReference,
  issuesPerInvoiceReferences,
  LeaseRemittanceConfiguration,
} from '../leaseRemittanceUtils';
import { getNumberOfMonthsFromFrequency } from '../leaseutils';
import { fromCents, roundAtSecondDecimal, toCents } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import {
  Invoice,
  InvoiceStatus,
  InvoiceType,
  InvoiceWithPostings,
  LeasePaymentFrequency,
  Posting,
  ReconciliationAllocationMode,
  ReconciliationReasonCode,
  Transaction,
} from '../types';

import { ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS, isAccountClassOrItsMirror } from './accountClasses';
import { withAwaitingPostingReason } from './awaitingPosting';
import { findStructuredCommunication } from './bankutils';
import { getCitedMonthInterval, parseCitedMonths } from './communicationMonthParser';
import { isPostingAllocatableUnderDispute } from './invoiceDispute';
import {
  attachInvoiceToPostings,
  getMatchingInvoicesWithExactAmount,
  getPostingsWithAmountLeftToPay,
  PaymentDraftContext,
  sortInvoicesByOldestDebt,
  withoutDraftsBehindOpenDebt,
} from './invoiceHelper';

const MAX_SUBSET_SEARCH_NODES = 20000;
const AMBIGUITY_THRESHOLD = 2;

export type LineAllocatablePredicate = (posting: Posting, invoice: InvoiceWithPostings) => boolean;

export interface LeasePaymentAllocationInput {
  amount: number;
  communications: (string | null | undefined)[];
  lease: LeaseRemittanceConfiguration;
  referenceDate: CalendarDate;
  paymentDate?: CalendarDate;
  payableInvoices: InvoiceWithPostings[];
  allocationMode: ReconciliationAllocationMode;
  overpaymentCapAmount: number;
  isLineAllocatable?: LineAllocatablePredicate;
}

export interface AllocationLine {
  posting: Posting;
  invoiceId: string;
  amountSelected: number;
}

export enum LeasePaymentAllocationOutcome {
  AUTO = 'AUTO',
  SUGGESTED = 'SUGGESTED',
  NONE = 'NONE',
}

export type LeasePaymentAllocation =
  | {
      outcome: LeasePaymentAllocationOutcome.AUTO;
      lines: AllocationLine[];
      invoiceIds: string[];
      pendingBalanceAmount: number;
      reasonCodes: ReconciliationReasonCode[];
    }
  | {
      outcome: LeasePaymentAllocationOutcome.SUGGESTED;
      reasonCode: ReconciliationReasonCode;
      alternatives: AllocationLine[][];
    }
  | { outcome: LeasePaymentAllocationOutcome.NONE };

interface OpenLine {
  posting: Posting;
  invoice: InvoiceWithPostings;
  remainingCents: number;
}

const NO_ALLOCATION: LeasePaymentAllocation = { outcome: LeasePaymentAllocationOutcome.NONE };

const withInvoiceOnPostings = (invoice: InvoiceWithPostings): InvoiceWithPostings => ({
  ...invoice,
  postings: attachInvoiceToPostings(invoice.postings, invoice),
});

const isProvisionLine = (posting: Posting): boolean =>
  isAccountClassOrItsMirror(posting.class, ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS);

const sortLinesProvisionsFirst = (lines: OpenLine[]): OpenLine[] =>
  sortBy(lines, [
    (line) => (isProvisionLine(line.posting) ? 0 : 1),
    (line) => line.posting.class,
    (line) => line.posting.id,
  ]);

const getOpenLines = (invoice: InvoiceWithPostings, isLineAllocatable: LineAllocatablePredicate): OpenLine[] =>
  sortLinesProvisionsFirst(
    getPostingsWithAmountLeftToPay(invoice.postings, true).reduce<OpenLine[]>((acc, posting) => {
      if (isLineAllocatable(posting, invoice)) {
        acc.push({ posting, invoice, remainingCents: toCents(posting.totalAmount) });
      }
      return acc;
    }, [])
  );

const hasExcludedOpenLine = (invoice: InvoiceWithPostings, isLineAllocatable: LineAllocatablePredicate): boolean =>
  getPostingsWithAmountLeftToPay(invoice.postings, true).some((posting) => !isLineAllocatable(posting, invoice));

// A draft is only settled as a whole document: a partial lettering would split an invoice that can still change
const isPartiallyCoverable = (lines: OpenLine[]): boolean =>
  !isEmpty(lines) && lines.every((line) => line.remainingCents > 0 && line.invoice.status !== InvoiceStatus.DRAFT);

const toAllocationLines = (lines: OpenLine[]): AllocationLine[] =>
  lines.map((line) => ({
    posting: line.posting,
    invoiceId: line.invoice.id,
    amountSelected: fromCents(line.remainingCents),
  }));

interface ExactMatch {
  lines: AllocationLine[];
  invoiceIds: string[];
}

const toExactMatch = (lines: AllocationLine[]): ExactMatch => ({
  lines,
  invoiceIds: uniq(lines.map((line) => line.invoiceId)),
});

const buildAutoAllocation = (
  { lines, invoiceIds }: ExactMatch,
  reasonCodes: ReconciliationReasonCode[],
  pendingBalanceAmount = 0
): LeasePaymentAllocation => ({
  outcome: LeasePaymentAllocationOutcome.AUTO,
  lines,
  invoiceIds,
  pendingBalanceAmount,
  reasonCodes,
});

const findExactDocuments = (
  invoices: InvoiceWithPostings[],
  amount: number,
  isLineAllocatable: LineAllocatablePredicate
): ExactMatch | undefined => {
  const candidates = invoices.filter((invoice) => !hasExcludedOpenLine(invoice, isLineAllocatable));
  const matchedInvoices = getMatchingInvoicesWithExactAmount(candidates, amount);
  if (isEmpty(matchedInvoices)) {
    return undefined;
  }
  return {
    lines: matchedInvoices.flatMap((matchedInvoice) =>
      toAllocationLines(getOpenLines(matchedInvoice, isLineAllocatable))
    ),
    invoiceIds: matchedInvoices.map((matchedInvoice) => matchedInvoice.id),
  };
};

type SubsetSearchResult = { solutions: OpenLine[][] } | { exhausted: true };

const findExactLineSubsets = (lines: OpenLine[], targetCents: number): SubsetSearchResult => {
  const suffixSums = lines.reduceRight<number[]>((acc, line) => [acc[0] + line.remainingCents, ...acc], [0]);
  const solutions: OpenLine[][] = [];
  let visitedNodes = 0;
  let isExhausted = false;

  const search = (index: number, currentCents: number, selected: OpenLine[]): void => {
    if (solutions.length >= AMBIGUITY_THRESHOLD || isExhausted) {
      return;
    }
    visitedNodes += 1;
    if (visitedNodes > MAX_SUBSET_SEARCH_NODES) {
      isExhausted = true;
      return;
    }
    if (currentCents === targetCents) {
      solutions.push(selected);
      return;
    }
    if (currentCents > targetCents || currentCents + suffixSums[index] < targetCents) {
      return;
    }
    lines.slice(index).forEach((line, offset) => {
      search(index + offset + 1, currentCents + line.remainingCents, [...selected, line]);
    });
  };

  search(0, 0, []);
  return isExhausted ? { exhausted: true } : { solutions };
};

interface FifoResult {
  lines: AllocationLine[];
  leftoverCents: number;
}

const allocateFifo = (
  invoices: InvoiceWithPostings[],
  amountCents: number,
  isLineAllocatable: LineAllocatablePredicate
): FifoResult =>
  sortInvoicesByOldestDebt(invoices).reduce<FifoResult>(
    (result, invoice) =>
      getOpenLines(invoice, isLineAllocatable).reduce<FifoResult>((lineResult, line) => {
        if (lineResult.leftoverCents === 0) {
          return lineResult;
        }
        const allocatedCents = Math.min(line.remainingCents, lineResult.leftoverCents);
        return {
          lines: [
            ...lineResult.lines,
            { posting: line.posting, invoiceId: line.invoice.id, amountSelected: fromCents(allocatedCents) },
          ],
          leftoverCents: lineResult.leftoverCents - allocatedCents,
        };
      }, result),
    { lines: [], leftoverCents: amountCents }
  );

const doesLineCoverMonth = (posting: Posting, month: CalendarDate): boolean => {
  if (isNil(posting.periodFrom) || isNil(posting.periodTo)) {
    return false;
  }
  const { start, end } = getCitedMonthInterval(month);
  return (
    isBeforeOrEqual(toCalendarDate(posting.periodFrom), end) && isAfterOrEqual(toCalendarDate(posting.periodTo), start)
  );
};

const getInvoicesOfCitedMonths = (
  invoices: InvoiceWithPostings[],
  citedMonths: CalendarDate[],
  isLineAllocatable: LineAllocatablePredicate
): InvoiceWithPostings[] =>
  invoices.filter((invoice) =>
    getOpenLines(invoice, isLineAllocatable).some((line) =>
      citedMonths.some((month) => doesLineCoverMonth(line.posting, month))
    )
  );

type ExactResolution =
  | { match: ExactMatch; reasonCode: ReconciliationReasonCode }
  | { ambiguous: AllocationLine[][] }
  | undefined;

// A channel that sends the reference as twelve bare digits only has it printed in the canonical field
export const getPaymentCommunications = (
  transaction: Pick<Transaction, 'structuredCommunication' | 'remittanceInformation' | 'additionalInformation'>
): (string | null | undefined)[] => [
  transaction.structuredCommunication,
  transaction.remittanceInformation,
  transaction.additionalInformation,
];

// A fixed reference, current or former, names the lease and never one document: only a document issued with
// its own reference can be named, and the lease's fixed references are set aside
const getInvoicesOfCitedReferences = (
  invoices: InvoiceWithPostings[],
  communications: (string | null | undefined)[],
  lease: LeaseRemittanceConfiguration
): InvoiceWithPostings[] => {
  if (!issuesPerInvoiceReferences(lease)) {
    return [];
  }
  const fixedReferences = getLeaseFixedReferences(lease);
  const citedReferences = uniq(
    compact(communications.map((communication) => findStructuredCommunication(communication)))
  ).filter((reference) => !fixedReferences.includes(reference));
  if (isEmpty(citedReferences)) {
    return [];
  }
  return invoices.filter((invoice) => {
    const invoiceReference = findStructuredCommunication(invoice.remittanceInformation);
    return (
      !isNil(invoiceReference) &&
      citedReferences.includes(invoiceReference) &&
      isInvoiceIssuedWithItsOwnReference(lease, invoice)
    );
  });
};

const NO_ALTERNATIVE: AllocationLine[][] = [];

const resolveExactly = (
  invoices: InvoiceWithPostings[],
  amount: number,
  isLineAllocatable: LineAllocatablePredicate
): ExactResolution => {
  const exactDocuments = findExactDocuments(invoices, amount, isLineAllocatable);
  if (!isNil(exactDocuments)) {
    return { match: exactDocuments, reasonCode: ReconciliationReasonCode.EXACT_DOCUMENTS };
  }
  const coverableLines = sortInvoicesByOldestDebt(invoices).flatMap((invoice) => {
    const lines = getOpenLines(invoice, isLineAllocatable);
    return isPartiallyCoverable(lines) ? lines : [];
  });
  const subsetSearch = findExactLineSubsets(coverableLines, toCents(amount));
  if ('exhausted' in subsetSearch) {
    return { ambiguous: NO_ALTERNATIVE };
  }
  if (isEmpty(subsetSearch.solutions)) {
    return undefined;
  }
  if (subsetSearch.solutions.length >= AMBIGUITY_THRESHOLD) {
    return { ambiguous: subsetSearch.solutions.map(toAllocationLines) };
  }
  return {
    match: toExactMatch(toAllocationLines(subsetSearch.solutions[0])),
    reasonCode: ReconciliationReasonCode.EXACT_SUBSET,
  };
};

const getPartiallyCoverableInvoices = (invoices: InvoiceWithPostings[], isLineAllocatable: LineAllocatablePredicate) =>
  invoices.filter((invoice) => isPartiallyCoverable(getOpenLines(invoice, isLineAllocatable)));

const toAmountSelectedByPostingId = (lines: AllocationLine[]): Record<string, number> =>
  Object.fromEntries(lines.map((line) => [line.posting.id, line.amountSelected]));

const haveSameLines = (first: FifoResult, second: FifoResult): boolean =>
  isEqual(toAmountSelectedByPostingId(first.lines), toAmountSelectedByPostingId(second.lines));

const buildOldestFirstAllocation = (
  oldestFirst: FifoResult,
  overpaymentCapAmount: number,
  leadingReasonCodes: ReconciliationReasonCode[]
): LeasePaymentAllocation => {
  const pendingBalanceAmount = fromCents(oldestFirst.leftoverCents);
  if (pendingBalanceAmount > overpaymentCapAmount) {
    return {
      outcome: LeasePaymentAllocationOutcome.SUGGESTED,
      reasonCode: ReconciliationReasonCode.ABNORMAL_OVERPAYMENT,
      alternatives: [oldestFirst.lines],
    };
  }
  const reasonCodes = [
    ...leadingReasonCodes,
    ...(isEmpty(oldestFirst.lines) ? [] : [ReconciliationReasonCode.FIFO_PROVISIONS_FIRST]),
    ...(pendingBalanceAmount > 0 ? [ReconciliationReasonCode.PENDING_BALANCE] : []),
  ];
  return buildAutoAllocation(toExactMatch(oldestFirst.lines), reasonCodes, pendingBalanceAmount);
};

const hasOpenDebt = (invoice: InvoiceWithPostings, isLineAllocatable: LineAllocatablePredicate): boolean =>
  getOpenLines(invoice, isLineAllocatable).some((line) => line.remainingCents > 0);

const getExactMatchCandidates = (
  invoices: InvoiceWithPostings[],
  namedInvoices: InvoiceWithPostings[],
  isLineAllocatable: LineAllocatablePredicate,
  paymentDate?: CalendarDate
): InvoiceWithPostings[] =>
  withoutDraftsBehindOpenDebt(invoices, (invoice) => hasOpenDebt(invoice, isLineAllocatable), {
    paymentDate,
    namedInvoiceIds: namedInvoices.map(({ id }) => id),
  });

const resolveLeasePaymentAllocation = ({
  amount,
  communications,
  lease,
  referenceDate,
  paymentDate,
  payableInvoices,
  allocationMode,
  overpaymentCapAmount,
  isLineAllocatable = isPostingAllocatableUnderDispute,
}: LeasePaymentAllocationInput): LeasePaymentAllocation => {
  const invoices = payableInvoices.map(withInvoiceOnPostings);
  const isAutomatic = allocationMode === ReconciliationAllocationMode.AUTOMATIC;
  const citedInvoices = getInvoicesOfCitedMonths(
    invoices,
    parseCitedMonths(communications, referenceDate),
    isLineAllocatable
  );
  const referencedInvoices = amount > 0 ? getInvoicesOfCitedReferences(invoices, communications, lease) : [];
  const exactMatchCandidates = getExactMatchCandidates(
    invoices,
    [...citedInvoices, ...referencedInvoices],
    isLineAllocatable,
    paymentDate
  );

  if (!isAutomatic || amount <= 0) {
    const referencedDocuments = isEmpty(referencedInvoices)
      ? undefined
      : findExactDocuments(referencedInvoices, amount, isLineAllocatable);
    if (!isNil(referencedDocuments)) {
      return buildAutoAllocation(referencedDocuments, [
        ReconciliationReasonCode.DOCUMENT_REFERENCE_CITED,
        ReconciliationReasonCode.EXACT_DOCUMENTS,
      ]);
    }
    const exactDocuments = findExactDocuments(exactMatchCandidates, amount, isLineAllocatable);
    return isNil(exactDocuments)
      ? NO_ALLOCATION
      : buildAutoAllocation(exactDocuments, [ReconciliationReasonCode.EXACT_DOCUMENTS]);
  }

  const amountCents = toCents(amount);
  const coverableInvoices = getPartiallyCoverableInvoices(invoices, isLineAllocatable);
  const globalFifo = allocateFifo(coverableInvoices, amountCents, isLineAllocatable);

  if (!isEmpty(referencedInvoices)) {
    const referencedResolution = resolveExactly(referencedInvoices, amount, isLineAllocatable);
    if (!isNil(referencedResolution) && 'match' in referencedResolution) {
      return buildAutoAllocation(referencedResolution.match, [
        ReconciliationReasonCode.DOCUMENT_REFERENCE_CITED,
        referencedResolution.reasonCode,
      ]);
    }
    const referencedFifo = allocateFifo(
      getPartiallyCoverableInvoices(referencedInvoices, isLineAllocatable),
      amountCents,
      isLineAllocatable
    );
    if (!isEmpty(referencedFifo.lines) && referencedFifo.leftoverCents === 0) {
      return buildAutoAllocation(toExactMatch(referencedFifo.lines), [
        ReconciliationReasonCode.DOCUMENT_REFERENCE_CITED,
        ReconciliationReasonCode.FIFO_PROVISIONS_FIRST,
      ]);
    }
  }

  if (!isEmpty(citedInvoices)) {
    const citedResolution = resolveExactly(citedInvoices, amount, isLineAllocatable);
    if (!isNil(citedResolution) && 'match' in citedResolution) {
      return buildAutoAllocation(citedResolution.match, [
        ReconciliationReasonCode.MONTH_CITED,
        citedResolution.reasonCode,
      ]);
    }
    const citedFifo = allocateFifo(
      getPartiallyCoverableInvoices(citedInvoices, isLineAllocatable),
      amountCents,
      isLineAllocatable
    );
    if (isNil(citedResolution) && haveSameLines(citedFifo, globalFifo)) {
      return buildOldestFirstAllocation(globalFifo, overpaymentCapAmount, [ReconciliationReasonCode.MONTH_CITED]);
    }
    return {
      outcome: LeasePaymentAllocationOutcome.SUGGESTED,
      reasonCode: ReconciliationReasonCode.AMBIGUOUS_MONTH_CITED,
      alternatives: [globalFifo.lines, citedFifo.lines],
    };
  }

  const resolution = resolveExactly(exactMatchCandidates, amount, isLineAllocatable);
  if (!isNil(resolution)) {
    if ('ambiguous' in resolution) {
      return {
        outcome: LeasePaymentAllocationOutcome.SUGGESTED,
        reasonCode: ReconciliationReasonCode.AMBIGUOUS_EXACT_SUBSET,
        alternatives: resolution.ambiguous,
      };
    }
    return buildAutoAllocation(resolution.match, [resolution.reasonCode]);
  }

  return buildOldestFirstAllocation(globalFifo, overpaymentCapAmount, []);
};

export const allocateLeasePayment = (input: LeasePaymentAllocationInput): LeasePaymentAllocation => {
  const allocation = resolveLeasePaymentAllocation(input);
  if (allocation.outcome !== LeasePaymentAllocationOutcome.AUTO) {
    return allocation;
  }
  return {
    ...allocation,
    reasonCodes: withAwaitingPostingReason(allocation.reasonCodes, allocation.invoiceIds, input.payableInvoices),
  };
};

export type LeasePaymentPreCheckInput = Omit<LeasePaymentAllocationInput, 'allocationMode' | 'overpaymentCapAmount'>;

export enum LeasePaymentPreCheckOutcome {
  PROPOSED = 'PROPOSED',
  AMBIGUOUS = 'AMBIGUOUS',
  NONE = 'NONE',
}

export type LeasePaymentPreCheck =
  | {
      outcome: LeasePaymentPreCheckOutcome.PROPOSED;
      lines: AllocationLine[];
      invoiceIds: string[];
      reasonCodes: ReconciliationReasonCode[];
    }
  | {
      outcome: LeasePaymentPreCheckOutcome.AMBIGUOUS;
      reasonCode: ReconciliationReasonCode;
      alternatives: AllocationLine[][];
    }
  | { outcome: LeasePaymentPreCheckOutcome.NONE };

const NO_PRE_CHECK: LeasePaymentPreCheck = { outcome: LeasePaymentPreCheckOutcome.NONE };

const toPreCheck = (
  resolution: ExactResolution,
  leadingReasonCodes: ReconciliationReasonCode[],
  ambiguousReasonCode: ReconciliationReasonCode
): LeasePaymentPreCheck | undefined => {
  if (isNil(resolution)) {
    return;
  }
  if ('ambiguous' in resolution) {
    return isEmpty(resolution.ambiguous)
      ? undefined
      : {
          outcome: LeasePaymentPreCheckOutcome.AMBIGUOUS,
          reasonCode: ambiguousReasonCode,
          alternatives: resolution.ambiguous,
        };
  }
  return {
    outcome: LeasePaymentPreCheckOutcome.PROPOSED,
    lines: resolution.match.lines,
    invoiceIds: resolution.match.invoiceIds,
    reasonCodes: [...leadingReasonCodes, resolution.reasonCode],
  };
};

// Unlike the automatic strategy, the pre-check never falls back on the oldest-first cover, whatever the mode
const resolveLeasePaymentPreCheck = ({
  amount,
  communications,
  lease,
  referenceDate,
  paymentDate,
  payableInvoices,
  isLineAllocatable = isPostingAllocatableUnderDispute,
}: LeasePaymentPreCheckInput): LeasePaymentPreCheck => {
  const invoices = payableInvoices.map(withInvoiceOnPostings);
  const citedInvoices = getInvoicesOfCitedMonths(
    invoices,
    parseCitedMonths(communications, referenceDate),
    isLineAllocatable
  );
  if (amount <= 0) {
    const exactDocuments = findExactDocuments(
      getExactMatchCandidates(invoices, citedInvoices, isLineAllocatable, paymentDate),
      amount,
      isLineAllocatable
    );
    return isNil(exactDocuments)
      ? NO_PRE_CHECK
      : {
          outcome: LeasePaymentPreCheckOutcome.PROPOSED,
          lines: exactDocuments.lines,
          invoiceIds: exactDocuments.invoiceIds,
          reasonCodes: [ReconciliationReasonCode.EXACT_DOCUMENTS],
        };
  }

  const referencedInvoices = getInvoicesOfCitedReferences(invoices, communications, lease);
  const referencedPreCheck = isEmpty(referencedInvoices)
    ? undefined
    : toPreCheck(
        resolveExactly(referencedInvoices, amount, isLineAllocatable),
        [ReconciliationReasonCode.DOCUMENT_REFERENCE_CITED],
        ReconciliationReasonCode.AMBIGUOUS_EXACT_SUBSET
      );
  const citedPreCheck = isEmpty(citedInvoices)
    ? undefined
    : toPreCheck(
        resolveExactly(citedInvoices, amount, isLineAllocatable),
        [ReconciliationReasonCode.MONTH_CITED],
        ReconciliationReasonCode.AMBIGUOUS_MONTH_CITED
      );
  return (
    referencedPreCheck ??
    citedPreCheck ??
    toPreCheck(
      resolveExactly(
        getExactMatchCandidates(invoices, [...citedInvoices, ...referencedInvoices], isLineAllocatable, paymentDate),
        amount,
        isLineAllocatable
      ),
      [],
      ReconciliationReasonCode.AMBIGUOUS_EXACT_SUBSET
    ) ??
    NO_PRE_CHECK
  );
};

export const proposeLeasePaymentPreCheck = (input: LeasePaymentPreCheckInput): LeasePaymentPreCheck => {
  const preCheck = resolveLeasePaymentPreCheck(input);
  if (preCheck.outcome !== LeasePaymentPreCheckOutcome.PROPOSED) {
    return preCheck;
  }
  return {
    ...preCheck,
    reasonCodes: withAwaitingPostingReason(preCheck.reasonCodes, preCheck.invoiceIds, input.payableInvoices),
  };
};

export const getLatestRentCallAmount = (
  invoices: Pick<Invoice, 'amount' | 'type' | 'creditNote' | 'status' | 'dueDate' | 'invoiceDate' | 'createdAt'>[]
): number | undefined =>
  last(
    sortInvoicesByOldestDebt(
      invoices.filter(
        (invoice) =>
          invoice.type === InvoiceType.RENT &&
          !invoice.creditNote &&
          invoice.status !== InvoiceStatus.DRAFT &&
          invoice.status !== InvoiceStatus.REVERSED
      )
    )
  )?.amount;

export const getOverpaymentCapAmount = (
  capMonths: number,
  paymentFrequency: LeasePaymentFrequency | null | undefined,
  latestCallAmount: number | null | undefined,
  totalRentalPrice: number | null | undefined
): number => {
  const frequencyMonths = getNumberOfMonthsFromFrequency(paymentFrequency ?? LeasePaymentFrequency.MONTHLY);
  const monthsPerCall = frequencyMonths > 0 ? frequencyMonths : 1;
  const monthlyCall = isNil(latestCallAmount) ? (totalRentalPrice ?? 0) : Math.abs(latestCallAmount) / monthsPerCall;
  return roundAtSecondDecimal(capMonths * monthlyCall);
};

const AWS_DATE_LENGTH = 10;

export const getAllocationReferenceDate = (statementDate: string | null | undefined): CalendarDate => {
  if (isNilOrEmpty(statementDate)) {
    return today();
  }
  return statementDate.length > AWS_DATE_LENGTH
    ? calendarDateFromTimestamp(statementDate)
    : toCalendarDate(statementDate);
};

export const getPaymentDate = (
  transaction: Pick<Transaction, 'statementDate' | 'operationDate'>
): CalendarDate | undefined =>
  head(
    [transaction.statementDate, transaction.operationDate]
      .reduce<
        CalendarDate[]
      >((acc, date) => (isNilOrEmpty(date) ? acc : [...acc, getAllocationReferenceDate(date)]), [])
      .sort(compareCalendarDates)
  );

export interface PaymentDraftContextInput {
  transaction: Pick<
    Transaction,
    'statementDate' | 'operationDate' | 'structuredCommunication' | 'remittanceInformation' | 'additionalInformation'
  >;
  lease?: LeaseRemittanceConfiguration | null;
  invoices: InvoiceWithPostings[];
  amount: number;
}

export const getPaymentDraftContext = ({
  transaction,
  lease,
  invoices,
  amount,
}: PaymentDraftContextInput): PaymentDraftContext => {
  const communications = getPaymentCommunications(transaction);
  const invoicesWithPostingInvoice = invoices.map(withInvoiceOnPostings);
  const namedInvoices = [
    ...getInvoicesOfCitedMonths(
      invoicesWithPostingInvoice,
      parseCitedMonths(communications, getAllocationReferenceDate(transaction.statementDate)),
      isPostingAllocatableUnderDispute
    ),
    ...(amount > 0 && !isNil(lease)
      ? getInvoicesOfCitedReferences(invoicesWithPostingInvoice, communications, lease)
      : []),
  ];
  return { paymentDate: getPaymentDate(transaction), namedInvoiceIds: namedInvoices.map(({ id }) => id) };
};
